import { Ellipsis } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/shared/auth';
import { cn } from '@/shared/lib/cn';
import type { NavSearch } from './adminNavigation';
import { NavCount, NavEntryLink } from './NavEntryLink';
import { activeEntryIn, type ActivePlace } from './navVisibility';
import { needsMore, phoneNavigation } from './phoneNavigation';
import { PhoneMoreSheet } from './PhoneMoreSheet';
import type { NavCounts } from './useNavCounts';

const tabClass =
  'relative flex min-h-[54px] flex-col items-center justify-center gap-[3px] rounded-control text-[11px] font-semibold no-underline';

interface PhoneTabBarProps {
  place: ActivePlace;
  path: string;
  search: NavSearch;
  counts: NavCounts;
}

/** Leiste unten am Handy, je Rolle; alles Weitere steht im Blatt „Mehr“. */
export function PhoneTabBar({ place, path, search, counts }: PhoneTabBarProps) {
  const { user } = useAuth();
  const nav = phoneNavigation(user);
  const [moreOpen, setMoreOpen] = useState(false);
  const more = needsMore(nav);
  const single = nav.tabs.length > 0 && nav.tabs.every((tab) => tab.area === nav.tabs[0]?.area);
  const activeSub =
    single && nav.tabs[0] ? activeEntryIn(nav.tabs[0].area.entries, path, search) : null;
  const inMore = place?.kind === 'area' && nav.moreAreas.some((area) => area.id === place.area.id);
  const columns = nav.tabs.length + (more ? 1 : 0);
  return (
    <nav
      aria-label="Hauptnavigation"
      className="border-hairline grid flex-none gap-0.5 border-t bg-white px-2 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {nav.tabs.map((tab) => {
        const active = single
          ? tab.entry === activeSub
          : place?.kind === 'area' && place.area.id === tab.area.id;
        const countKey = tab.area.count ?? tab.area.entries.find((e) => e.count)?.count;
        const count = !single && countKey ? counts[countKey] : undefined;
        return (
          <NavEntryLink
            key={tab.key}
            entry={tab.entry}
            active={active}
            className={cn(tabClass, active ? 'bg-ink text-white' : 'text-ink-soft')}
          >
            <tab.icon size={22} aria-hidden />
            {tab.label}
            {count ? (
              <NavCount
                value={count}
                hot={countKey === 'revisionHolds'}
                className="absolute right-[18%] top-1 h-[18px] min-w-[18px] border-2 border-white px-1 text-[10px]"
              />
            ) : null}
          </NavEntryLink>
        );
      })}
      {more && (
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-haspopup="dialog"
          className={cn(tabClass, moreOpen || inMore ? 'bg-ink text-white' : 'text-ink-soft')}
        >
          <Ellipsis size={22} aria-hidden />
          Mehr
        </button>
      )}
      {more && (
        <PhoneMoreSheet nav={nav} open={moreOpen} onOpenChange={setMoreOpen} place={place} />
      )}
    </nav>
  );
}
